import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, ClickAwayListener } from '@material-ui/core';
import ArrowTooltip from '../arrowTooltip';
import VirtualizedList from '../virtualizedList';
import CustomButton from '../customButton';
import VirtualItem from './virtualItem';
import { isArray, isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  tooltipPopper: {
    cursor: 'default',
    pointerEvents: 'auto',
    minWidth: 308,
  },
  tooltipWrapper: {
    minWidth: 308,
    padding: spacing(6),
  },
  tooltipItemsWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  tooltipViewport: {
    maxHeight: 220,
  },
  tooltipTitle: {
    color: `${primary.bluish2}`,
    wordBreak: 'break-word',
    textAlign: 'left',
    marginBottom: spacing(2),
  },
  emptyLabel: {
    color: primary.bluish2,
    wordBreak: 'break-word',
    textAlign: 'left',
  },
  tooltipItem: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  contentContainer: {
    paddingBottom: spacing(2),
    paddingTop: spacing(2),
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      marginBottom: 0,
      borderBottom: 'none',
    },
  },
  childrenContainer: {
    cursor: 'pointer',
  },
  childrenContainerDisabled: {
    cursor: 'default',
  },
  scrollY: {
    right: -15,
  },
  tooltipButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: spacing(3),
  },
  buttonWrapper: {
    display: 'flex',
  },
}));

const AsyncListTooltip = ({
  tooltipPopperClass = undefined,
  tooltipWrapperClass = undefined,
  tooltipItemsWrapperClass = undefined,
  tooltipViewportClass = undefined,
  title = '',
  tooltipButtonLabel = '',
  emptyLabel='',
  labelData = [],
  getLabelData = () => {},
  labelComponent = null,
  labelComponentProps = {},
  renderLabelItem = () => {},
  children,
  tooltipButtonProps = {},
  addTooltipButtonProps = {},
  isArrowHidden = false,
  isDisabled = false,
  hasTooltipButton = false,
  hasAddButton = false,
  addButtonLabel = '',
  isTooltipDisabled = false,
  shouldDisablePortal = true,
  isItemClickable = () => true,
  onToggleView = () => {},
  onTooltipButtonClickHandler = () => {},
  onAddTooltipButtonClickHandler = () => {},
  onItemClickHandler = () => {},
  getItemSize = null,
  ...rest
}) => {
  const classes = useStyles();

  const [isOpened, setIsOpened] = useState(false);
  const [items, setItems] = useState(null);
  const previousLabelDataLengthRef = useRef(0);

  const getVirtualItemSize = useCallback(
    index => {
      if (getItemSize) {
        return getItemSize(index, items);
      }
      return index === items.length - 1 ? 24 : 32;
    },
    [getItemSize, items]
  );
  const handleCloseTooltip = useCallback(() => {
    setIsOpened(false);
    onToggleView(true);
  }, [onToggleView]);

  useEffect(() => {
    if (!isArray(labelData)) return;

    const previousLength = previousLabelDataLengthRef.current;
    previousLabelDataLengthRef.current = labelData.length;

    if (!isOpened) return;

    if (!isArrayEmpty(labelData)) {
      setItems(labelData);
      return;
    }

    if (previousLength > 0) {
      handleCloseTooltip();
    }
  }, [isOpened, labelData, handleCloseTooltip]);

  const onTooltipContentClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTooltipButtonClick = e => {
    e.preventDefault();
    e.stopPropagation();
    handleCloseTooltip();
    return onTooltipButtonClickHandler();
  };

  const handleAddTooltipButtonClick = e => {
    e.preventDefault();
    e.stopPropagation();
    handleCloseTooltip();
    return onAddTooltipButtonClickHandler();
  };

  const handleOpenTooltip = async e => {
    if (!isOpened) {
      e.preventDefault();
      e.stopPropagation();
      let tooltipItems;
      if (!isArrayEmpty(labelData)) {
        tooltipItems = labelData;
      } else {
        tooltipItems = await getLabelData().then(({ data }) => data);
      }

      onToggleView(false);
      setItems(tooltipItems?.results || tooltipItems);
      setIsOpened(true);
    } else {
      handleCloseTooltip();
    }
  };

  const renderItem = useCallback(
    item => {
      const isAccessible = !isDisabled && isItemClickable(item);

      return (
        <VirtualItem
          item={item}
          isAccessible={isAccessible}
          renderLabelItem={renderLabelItem}
          labelComponent={labelComponent}
          labelComponentProps={labelComponentProps}
          onItemClickHandler={onItemClickHandler}
        />
      );
    },
    [
      isDisabled,
      renderLabelItem,
      isItemClickable,
      labelComponent,
      labelComponentProps,
      onItemClickHandler,
    ]
  );

  const hasItems = isArray(items) && !isArrayEmpty(items);

  const renderTooltipLabel = () => {
    if (isArray(items) && !hasItems && emptyLabel) {
      return (
        <Typography className={classes.emptyLabel}>{emptyLabel}</Typography>
      );
    }

    return (
      (hasItems || hasTooltipButton) && (
        <div>
          {title && (
            <Typography variant="h5" className={classes.tooltipTitle}>
              {`${title}:`}
            </Typography>
          )}
          {hasItems && (
            <div
              className={classNames(
                classes.tooltipItemsWrapper,
                tooltipItemsWrapperClass
              )}
            >
              <VirtualizedList
                verticalScrollClass={classes.scrollY}
                viewportClass={classNames(
                  classes.tooltipViewport,
                  tooltipViewportClass
                )}
                itemClass={classes.tooltipItem}
                items={items}
                count={items?.length ?? 0}
                gap={8}
                estimateSize={getVirtualItemSize}
                renderItem={renderItem}
              />
            </div>
          )}
          <div className={classes.buttonWrapper}>
            {hasAddButton && (
              <div className={classes.tooltipButtonWrapper}>
                <CustomButton
                  type="addSmallDarkRoundedOutlined"
                  onClick={handleAddTooltipButtonClick}
                  {...addTooltipButtonProps}
                >
                  {addButtonLabel}
                </CustomButton>
              </div>
            )}
            {hasTooltipButton && (
              <div className={classes.tooltipButtonWrapper}>
                <CustomButton
                  type="addDarkRoundedOutlined"
                  onClick={handleTooltipButtonClick}
                  {...tooltipButtonProps}
                >
                  {tooltipButtonLabel}
                </CustomButton>
              </div>
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={handleCloseTooltip}
    >
      <div onClick={onTooltipContentClick}>
        <ArrowTooltip
          open={isOpened}
          tooltipPopperClassName={classNames(
            classes.tooltipPopper,
            tooltipPopperClass
          )}
          tooltipClassName={classNames(
            classes.tooltipWrapper,
            tooltipWrapperClass
          )}
          tooltipLabel={renderTooltipLabel()}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          shouldDisablePortal={shouldDisablePortal}
          interactive
          isArrowHidden={isArrowHidden}
          {...rest}
        >
          <div
            className={classNames(classes.childrenContainer, {
              [classes.childrenContainerDisabled]: isTooltipDisabled,
            })}
            onClick={!isTooltipDisabled ? handleOpenTooltip : () => {}}
          >
            {children}
          </div>
        </ArrowTooltip>
      </div>
    </ClickAwayListener>
  );
};

AsyncListTooltip.propTypes = {
  tooltipPopperClass: PropTypes.string,
  tooltipWrapperClass: PropTypes.string,
  tooltipItemsWrapperClass: PropTypes.string,
  tooltipViewportClass: PropTypes.string,
  addButtonLabel: PropTypes.string,
  tooltipButtonProps: PropTypes.object,
  addTooltipButtonProps: PropTypes.object,
  title: PropTypes.string,
  emptyLabel: PropTypes.string,
  tooltipButtonLabel: PropTypes.string,
  children: PropTypes.element.isRequired,
  isDisabled: PropTypes.bool,
  isTooltipDisabled: PropTypes.bool,
  labelData: PropTypes.array,
  labelComponentProps: PropTypes.object,
  hasTooltipButton: PropTypes.bool,
  hasAddButton: PropTypes.bool,
  isArrowHidden: PropTypes.bool,
  onToggleView: PropTypes.func,
  onItemClickHandler: PropTypes.func,
  isItemClickable: PropTypes.func,
  getLabelData: PropTypes.func,
  renderLabelItem: PropTypes.func,
  labelComponent: PropTypes.elementType,
  shouldDisablePortal: PropTypes.bool,
  onTooltipButtonClickHandler: PropTypes.func,
  onAddTooltipButtonClickHandler: PropTypes.func,
  getItemSize: PropTypes.func,
};

export default AsyncListTooltip;
